import React, { Fragment } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Grid, Typography } from "@material-ui/core";
import { CategoryTag, Corporation, Favourite, Meeting, ExternalDocument, Locale, Participant, Event, ParticipantRole, Exhibitor, Classification } from "../../core/api/model";
import SpinnerModal from '../../core/spinner-modal';
import { DashboardMeetingsPanel } from "../../components/DashboardMeetingsPanel";
import { DashboardFavouritesPanel } from "../../components/DashboardFavouritesPanel";
import { DashboardDocumentsPanel } from "../../components/DashboardDocumentsPanel";
import { DashboardExpertsPanel } from "../../components/DashboardExpertsPanel";
import { DashboardClassificationsPanel } from "../../components/DashboardClassificationsPanel";

// @TODO REFACTORING use custom hooks

export interface UserQueryVariables {
  expoCode: string;
}

export interface UserQueryData {
  participant: Participant[];
}

export interface DashboardQueryVariables {
  expoCode: string;
  partId: number;
  corpId?: number;
}

export interface DashboardQueryData {
  meetings: Meeting[];
  favourite: Favourite;
  externalDocumentList: ExternalDocument[];
  classification: Classification[];
  exhibitor?: Exhibitor;
}

export const USER_QUERY = gql`
  query User($expoCode: String!) {
    participant (expoCode: $expoCode, currentUser: true) {
        partId,
        corporation {
            corpId
        }
        partRole
    }
  }
`;


// @TODO REFACTORING use fragments to simplify the following two queries

export const DASHBOARD_QUERY = gql`
  query Dashboard($expoCode: String!, $partId: Int!) {
    meetings (partId: $partId) {
      id
      product {
        classificationTitle {
          de_DE
          en_GB
        }
      }
      participant {
        partId
        partName
        partSurname
        partFunction
      }
      expert {
        partId
        partName
        partSurname
        partFunction
        partCompany2
      }
      start
      end
      request
      status
    }
    favourite (expoCode: $expoCode, partId: $partId) {
      corporation {
          corpId
          expoSiteLink {
            de_DE
            en_GB
          }
          corpName {
              de_DE
              en_GB
          }
          corpLogo {
              url
              altText
          }
      }
      participant {
          partId
          expoSiteLink {
            de_DE
            en_GB
          }
          partName
          partSurname
          partPic (size: S160x160) {
              url
              altText
          }
      }
      event {
          eventId
          expoSiteLink {
            de_DE
            en_GB
          }
          eventTitle {
              de_DE
              en_GB
          }
          eventBegin(raw: true)
          eventPic(size: S120x120) {
              url
              altText
          }
      }
    }
    externalDocumentList (partId: $partId) {
      id
      partId
      externalUrl
      metaTitle
      metaDescription
      accessDate
    }
    classification {
      classificationId,
      classificationTitle {
          de_DE
          en_GB
      },
    }
  }
`;

export const DASHBOARD_EVENTMANAGER_QUERY = gql`
  query Dashboard($expoCode: String!, $partId: Int!, $corpId: Int!) {
    meetings (partId: $partId) {
      id
      product {
        classificationTitle {
          de_DE
          en_GB
        }
      }
      participant {
        partId
        partName
        partSurname
        partFunction
      }
      expert {
        partId
        partName
        partSurname
        partFunction
        partCompany2
      }
      start
      end
      request
      status
    }
    favourite (expoCode: $expoCode, partId: $partId) {
      corporation {
          corpId
          expoSiteLink {
            de_DE
            en_GB
          }
          corpName {
              de_DE
              en_GB
          }
          corpLogo {
              url
              altText
          }
      }
      participant {
          partId
          expoSiteLink {
            de_DE
            en_GB
          }
          partName
          partSurname
          partPic (size: S160x160) {
              url
              altText
          }
      }
      event {
          eventId
          expoSiteLink {
            de_DE
            en_GB
          }
          eventTitle {
              de_DE
              en_GB
          }
          eventBegin(raw: true)
          eventPic(size: S120x120) {
              url
              altText
          }
      }
    }
    externalDocumentList (partId: $partId) {
      id
      partId
      externalUrl
      metaTitle
      metaDescription
      accessDate
    }
    classification {
      classificationId,
      classificationTitle {
          de_DE
          en_GB
      },
    }
    exhibitor(expoCode: $expoCode, corpId: $corpId) {
      corpId
      experts {
        partId
        partName
        partSurname
        partFunction
        partEmail1
        partPic {
          url
          altText
        }
      }
      invitedExperts {
        email
      }
      classification {
        classificationId,
        classificationTitle {
            de_DE
            en_GB
        }
        expert {
          partId
          partName
          partSurname
          partFunction
          partEmail1
        }
      }
    }
  }
`;

export const DASHBOARD_FAVOURITE_MUTATION = gql`
  mutation Favourite(
    $elementId: Int!
    $partId: Int!
    $categoryTag: RecommendationElementType!
    $expoCode: String!
  ) {
      favourite (
          input: {
              elementId: $elementId
              partId: $partId
              categoryTag: $categoryTag
              expoCode: $expoCode
          }
      )
      {
        success
      }
  }
`;

export const DASHBOARD_EXPERT_ADD_MUTATION = gql`
  mutation ExpertAdd(
    $expoCode: String!
    $corpId: Int!
    $email: String!
  ) {
    updateExhibitor (
      expoCode: $expoCode
      corpId: $corpId
      add: { experts: [$email] }
    )
    {
      corpId
    }
  }
`;

export const DASHBOARD_EXPERT_DELETE_MUTATION = gql`
  mutation ExpertAdd(
    $expoCode: String!
    $corpId: Int!
    $email: String!
  ) {
    updateExhibitor (
      expoCode: $expoCode
      corpId: $corpId
      delete: { experts: [$email] }
    )
    {
      corpId
    }
  }
`;

export const DASHBOARD_CLASSIFICATION_ADD_MUTATION = gql`
  mutation ExpertAdd(
    $expoCode: String!
    $corpId: Int!
    $classificationId: Int!
    $partId: Int!
  ) {
    updateExhibitor (
      expoCode: $expoCode
      corpId: $corpId
      add: { classifications: [ { classification: $classificationId, expert: $partId} ] }
    )
    {
      corpId
    }
  }
`;

export const DASHBOARD_CLASSIFICATION_DELETE_MUTATION = gql`
  mutation ExpertAdd(
    $expoCode: String!
    $corpId: Int!
    $classificationId: Int!
  ) {
    updateExhibitor (
      expoCode: $expoCode
      corpId: $corpId
      delete: { classifications: [ { classification: $classificationId} ] }
    )
    {
      corpId
    }
  }
`;

export const DASHBOARD_CLASSIFICATION_DELETE_EXPERT_MUTATION = gql`
  mutation ExpertAdd(
    $expoCode: String!
    $corpId: Int!
    $classificationId: Int!
    $partId: Int!
  ) {
    updateExhibitor (
      expoCode: $expoCode
      corpId: $corpId
      delete: { classificationExperts: [ { classification: $classificationId, expert: $partId} ] }
    )
    {
      corpId
    }
  }
`;

export const DASHBOARD_CLASSIFICATION_CHANGE_EXPERT_MUTATION = DASHBOARD_CLASSIFICATION_ADD_MUTATION;

export interface ParticipantDashboardProps {
  locale: Locale;
  showErrorModal: (title: string, description: string, raw?: any) => void;
}

export const ParticipantDashboard = (props: ParticipantDashboardProps) => {
  
  const { data: userData  } = useQuery<UserQueryData, UserQueryVariables>(USER_QUERY, {variables: { expoCode: (process.env.REACT_APP_EXPO_CODE || '') }});

  const userIsEventmanager = () => userData?.participant[0].partRole.includes(ParticipantRole.Eventmanager) && userData?.participant[0].corporation.length > 0;

  const dashboardQueryVariables: DashboardQueryVariables = {
    expoCode: (process.env.REACT_APP_EXPO_CODE || ''), 
    partId: userData?.participant[0].partId || 0, 
  };
  const { data, loading, refetch } = 
  useQuery<DashboardQueryData, DashboardQueryVariables>(
    userIsEventmanager() ? 
      DASHBOARD_EVENTMANAGER_QUERY : 
      DASHBOARD_QUERY,
    { 
      variables: userIsEventmanager() ? 
        {...dashboardQueryVariables, corpId: (userData?.participant[0].corporation[0]?.corpId || 0)} : 
        dashboardQueryVariables,
      onError: (error) => {
        props.showErrorModal(intl.formatMessage({id: 'DashboardQueryErrorTitle'}), intl.formatMessage({id: 'DashboardQueryErrorDescription'}));
      }
    }
  );
  
  const [submitFavouriteMutation, {loading: pendingFavouriteMutation}] = useMutation(DASHBOARD_FAVOURITE_MUTATION);

  const deleteFavourite = (elementId: number, categoryTag: CategoryTag) => {
    submitFavouriteMutation({
      variables: { expoCode: (process.env.REACT_APP_EXPO_CODE || ''), partId: (userData?.participant[0].partId || 0), elementId: elementId, categoryTag: categoryTag } ,
      update(cache) {
        cache.modify({
          fields: {
            favourite(existingFavourite: Favourite[] = []) {
                return [
                  {
                    corporation: categoryTag === CategoryTag.Corporation ? existingFavourite[0].corporation.filter((corpRef: Corporation) => elementId !== corpRef.corpId) : existingFavourite[0].corporation,
                    participant: categoryTag === CategoryTag.Participant ? existingFavourite[0].participant.filter((partRef: Participant) => elementId !== partRef.partId) : existingFavourite[0].participant,
                    event: CategoryTag.Event ? existingFavourite[0].event.filter((eventRef: Event) => elementId !== eventRef.eventId) : existingFavourite[0].event,
                  }
                ]
            },
          },
        });
      },
    });
  }

  const [submitExpertAddMutation, {loading: pendingExpertAddMutation}] = useMutation(
    DASHBOARD_EXPERT_ADD_MUTATION, {
      onCompleted: data => {
        refetch(); // @TODO REFACTORING find better option but reflect that product panel might be changed as well
      },
      onError: (error) => {
        props.showErrorModal(intl.formatMessage({id: 'DashboardExpertAddMutationErrorTitle'}), intl.formatMessage({id: 'DashboardExpertAddMutationErrorDescription'}));
      }
    }
  );
  const [submitExpertDeleteMutation, {loading: pendingExpertDeleteMutation}] = useMutation(
    DASHBOARD_EXPERT_DELETE_MUTATION, {
      onCompleted: data => {
        refetch(); // @TODO REFACTORING find better option but reflect that product panel might be changed as well
      },
      onError: (error) => {
        props.showErrorModal(intl.formatMessage({id: 'DashboardExpertDeleteMutationErrorTitle'}), intl.formatMessage({id: 'DashboardExpertDeleteMutationErrorDescription'}));
      }
    }
  );
  const [submitClassificationAddMutation, {loading: pendingClassificationAddMutation}] = useMutation(
    DASHBOARD_CLASSIFICATION_ADD_MUTATION, {
      onCompleted: data => {
        refetch(); // @TODO REFACTORING find better option but reflect that product panel might be changed as well
      },
      onError: (error) => {
        props.showErrorModal(intl.formatMessage({id: 'DashboardClassificationAddMutationErrorTitle'}), intl.formatMessage({id: 'DashboardClassificationAddMutationErrorDescription'}));
      }
    }
  );
  const [submitClassificationDeleteMutation, {loading: pendingClassificationDeleteMutation}] = useMutation(
    DASHBOARD_CLASSIFICATION_DELETE_MUTATION, {
      onCompleted: data => {
        refetch(); // @TODO REFACTORING find better option but reflect that product panel might be changed as well
      },
      onError: (error) => {
        props.showErrorModal(intl.formatMessage({id: 'DashboardClassificationDeleteMutationErrorTitle'}), intl.formatMessage({id: 'DashboardClassificationDeleteMutationErrorDescription'}));
      }
    }
  );
  const [submitClassificationDeleteExpertMutation, {loading: pendingClassificationDeleteExpertMutation}] = useMutation(
    DASHBOARD_CLASSIFICATION_DELETE_EXPERT_MUTATION, {
      onCompleted: data => {
        refetch(); // @TODO REFACTORING find better option but reflect that product panel might be changed as well
      },
      onError: (error) => {
        props.showErrorModal(intl.formatMessage({id: 'DashboardClassificationDeleteExpertMutationErrorTitle'}), intl.formatMessage({id: 'DashboardClassificationDeleteExpertMutationErrorDescription'}));
      }
    }
  );

  const addExpert = (email: string) => {
    submitExpertAddMutation({variables: { 
      expoCode: (process.env.REACT_APP_EXPO_CODE || ''), 
      corpId: (userData?.participant[0].corporation[0].corpId || 0),
      email
    }});
  }

  const deleteExpert = (expert: Participant) => {
    const isExpertForClassification = (data?.exhibitor?.classification || []).findIndex((classification: Classification) => expert.partId === classification.expert?.partId) !== -1;
    
    if (isExpertForClassification) {
      props.showErrorModal(
        intl.formatMessage({id: 'DashboardExpertsDeleteBlockedTitle'}),
        intl.formatMessage({id: 'DashboardExpertsDeleteBlockedDescription'})
      )
    } else {
      deleteExpertForced(expert);
    }
  }
  
  const deleteExpertForced = (expert: Participant | undefined) => {
    submitExpertDeleteMutation({variables: { 
      expoCode: (process.env.REACT_APP_EXPO_CODE || ''), 
      corpId: (userData?.participant[0].corporation[0].corpId || 0),
      email: expert?.partEmail1,
    }});
    
  }

  const addClassification = (classificationId: number, partId: number) => {
    submitClassificationAddMutation({variables: { 
      expoCode: (process.env.REACT_APP_EXPO_CODE || ''), 
      corpId: (userData?.participant[0].corporation[0].corpId || 0),
      classificationId,
      partId,
    }});    
  }

  const deleteClassification = (classificationId: number) => {
    submitClassificationDeleteMutation({variables: { 
      expoCode: (process.env.REACT_APP_EXPO_CODE || ''), 
      corpId: (userData?.participant[0].corporation[0].corpId || 0),
      classificationId,
    }});
  }

  const editClassification = (classificationId: number, partId: number | undefined) => {
    if (partId) {
      submitClassificationAddMutation({variables: { 
        expoCode: (process.env.REACT_APP_EXPO_CODE || ''), 
        corpId: (userData?.participant[0].corporation[0].corpId || 0),
        classificationId,
        partId,
      }});
    } else {
      const oldPartId = data?.exhibitor?.classification.find(classification => classification.classificationId === classificationId)?.expert.partId;
      submitClassificationDeleteExpertMutation({variables: { 
        expoCode: (process.env.REACT_APP_EXPO_CODE || ''), 
        corpId: (userData?.participant[0].corporation[0].corpId || 0),
        classificationId,
        partId: oldPartId,
      }}); 
    }
  }

  const intl = useIntl();

  return (
    <Fragment>
      <SpinnerModal
        active={loading || pendingFavouriteMutation || pendingExpertAddMutation || pendingExpertDeleteMutation || pendingClassificationAddMutation ||pendingClassificationDeleteMutation || pendingClassificationDeleteExpertMutation}
      ></SpinnerModal>

      {/*
      <ConfirmDialog
        title={intl.formatMessage({id: 'DashboardExpertsDeleteTitle'})}
        confirmText={intl.formatMessage({id: 'DashboardExpertsDeleteConfirm'})}
        cancelText={intl.formatMessage({id: 'DashboardExpertsDeleteCancel'})}
        open={deleteExpertOpen}
        setOpen={setDeleteExpertOpen}
        onConfirm={() => deleteExpertForced(deleteExpertData)}
      >
        <FormattedMessage id='DashboardExpertsDeleteDescription' />
      </ConfirmDialog>
      */}

      <Box mb={{xs: 6, md: 7.5}}>
        <Typography component="h1" variant="h1" color='primary'>
          <FormattedMessage
            id='DashboardTitle'
            />
        </Typography>

        {/*<Typography>*/}
        {/*  <FormattedMessage*/}
        {/*    id='DashboardDescription'*/}
        {/*    />*/}
        {/*</Typography>*/}
      </Box>

      <Grid container spacing={5}>
        {/*REMOVED MEETINGPANEL REGARDING TO HMCMM-150 */}
        {/*<Grid item xs={12} md={6}>*/}
        {/*    <DashboardMeetingsPanel */}
        {/*      partId={(userData?.participant[0].partId || 0)}*/}
        {/*      locale={props.locale}*/}
        {/*      meetings={data?.meetings || []} */}
        {/*    />*/}
        {/*</Grid>*/}


        <Grid item xs={12} md={6}>

          <DashboardFavouritesPanel
            locale={props.locale}
            favourites={data?.favourite || { corporation: [], participant: [], event: [] }}
            deleteFavourite={deleteFavourite}
          />

        </Grid>
        <Grid item xs={12} md={6}>

            <DashboardDocumentsPanel
              locale={props.locale}
              documents={data?.externalDocumentList || []}
            />

        </Grid>
        {!userIsEventmanager() ? null : (
          <Fragment>
            <Grid item xs={12} md={6}>

                <DashboardExpertsPanel
                  experts={data?.exhibitor?.experts || []}
                  invitedExperts={data?.exhibitor?.invitedExperts || []}
                  addExpert={addExpert}
                  deleteExpert={deleteExpert}
                />

            </Grid>
            <Grid item xs={12}>

                <DashboardClassificationsPanel
                  locale={props.locale}
                  classifications={data?.exhibitor?.classification || []}
                  classificationsAll={data?.classification || []}
                  experts={data?.exhibitor?.experts || []}
                  addClassification={addClassification}
                  deleteClassification={deleteClassification}
                  editClassification={editClassification}
                />

            </Grid>
          </Fragment>
        )}
      </Grid>

    </Fragment>
  );
};
